import React, { createContext, useEffect, useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

import { setMenuColor, setPrimaryColor, setSecondaryColor } from '.';
import { Preferences } from '../../../Types/api';
import { AccountSettings, CloudPlatform, COLOR_PREFERENCES } from '../../../Types/cloudPlatform';
import { getContrastColor, WHITE } from '../../../Utils/color';

interface ThemeLoaderProps {
  children: (args: { loading: boolean; data?: CloudPlatform; accountSettings?: AccountSettings }) => React.ReactElement;
}

export interface ThemeContext {
  primaryColor: string;
  secondaryColor: string;
  menuColor: string;
  theme: 'light' | 'dark';
  loading: boolean;
}

export const themeContext = createContext<ThemeContext>({
  primaryColor: '#000',
  secondaryColor: '#000',
  menuColor: '#000',
  theme: 'dark',
  loading: true,
});

const ThemeLoader: React.FC<ThemeLoaderProps> = ({ children }) => {
  const { data, error } = useSWRImmutable<Preferences>('/preferences');

  const preferences = {} as Record<COLOR_PREFERENCES, string>;
  data?.data?.preferences.forEach(({ key, value }) => {
    preferences[key] = value;
  });
  const accountSettings = data?.data?.account_settings || [];
  const primaryColor = preferences[COLOR_PREFERENCES.PRIMARY];
  const secondaryColor = preferences[COLOR_PREFERENCES.SECONDARY];
  const menuColor = preferences[COLOR_PREFERENCES.MENU];

  const contextValue = useMemo<ThemeContext>(
    () => ({
      primaryColor,
      secondaryColor,
      menuColor,
      theme: getContrastColor(menuColor || '#000000') === WHITE ? 'light' : 'dark',
      loading: !error && !data,
    }),
    [data, error, menuColor, primaryColor, secondaryColor],
  );

  useEffect(() => {
    setPrimaryColor(primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    setSecondaryColor(secondaryColor);
  }, [secondaryColor]);

  useEffect(() => {
    setMenuColor(menuColor);
  }, [menuColor]);

  return (
    <themeContext.Provider value={contextValue}>
      {children({ loading: !error && !data, data: data?.data, accountSettings })}
    </themeContext.Provider>
  );
};

export default ThemeLoader;
